import { makeAutoObservable, runInAction } from 'mobx';
import {
  superadminGetClient,
  superadminGetClientBookings,
  auditLogsList,
} from '@pro-sto/api-client';
import { toastStore } from './ToastStore';

/** Shape returned by superadminGetClient (typed as unknown in generated code) */
export interface ClientDetail {
  uuid: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  created_at: string;
  [key: string]: unknown;
}

/** Shape of a single booking returned by superadminGetClientBookings */
export interface ClientBooking {
  uuid: string;
  start_time: string;
  end_time: string;
  status: string;
  total_cost: number | string;
  payment_status: string;
  payment_method: string | null;
  created_by_source: string;
  service_center_uuid: string;
  service_uuid: string;
  service?: { name?: string; [key: string]: unknown } | null;
  service_center?: { name?: string; [key: string]: unknown } | null;
  [key: string]: unknown;
}

/** Shape of a single audit log entry */
export interface AuditLogEntry {
  uuid: string;
  action: string;
  entity_type: string | null;
  entity_uuid: string | null;
  created_at: string;
  details?: Record<string, unknown> | null;
  [key: string]: unknown;
}

export type ClientDetailTab = 'bookings' | 'logs';

export class ClientDetailStore {
  client: ClientDetail | null = null;
  isLoadingClient = false;

  bookings: ClientBooking[] = [];
  isLoadingBookings = false;

  logs: AuditLogEntry[] = [];
  isLoadingLogs = false;
  logsHasMore = true;
  private logsOffset = 0;
  private readonly logsLimit = 30;

  activeTab: ClientDetailTab = 'bookings';

  constructor() {
    makeAutoObservable(this);
  }

  reset = () => {
    this.client = null;
    this.bookings = [];
    this.logs = [];
    this.logsOffset = 0;
    this.logsHasMore = true;
    this.activeTab = 'bookings';
  };

  setTab = (tab: ClientDetailTab) => {
    this.activeTab = tab;
  };

  loadClient = async (uuid: string) => {
    this.isLoadingClient = true;
    try {
      const response = await superadminGetClient({ uuid });
      runInAction(() => {
        this.client = response as unknown as ClientDetail;
        this.isLoadingClient = false;
      });
    } catch (error) {
      runInAction(() => { this.isLoadingClient = false; });
      toastStore.showError('Не удалось загрузить данные клиента');
      console.error('Failed to load client:', error);
    }
  };

  loadBookings = async (uuid: string) => {
    this.isLoadingBookings = true;
    try {
      const response = await superadminGetClientBookings({ uuid });
      runInAction(() => {
        const raw = response as { items?: unknown[] } | unknown[];
        this.bookings = (Array.isArray(raw) ? raw : (raw as { items?: unknown[] }).items ?? []) as ClientBooking[];
        this.isLoadingBookings = false;
      });
    } catch (error) {
      runInAction(() => { this.isLoadingBookings = false; });
      toastStore.showError('Не удалось загрузить бронирования');
      console.error('Failed to load bookings:', error);
    }
  };

  loadLogs = async (clientUuid: string) => {
    this.isLoadingLogs = true;
    this.logsOffset = 0;
    try {
      const response = await auditLogsList({ clientUuid, limit: this.logsLimit, offset: 0 });
      const raw = response as Record<string, unknown>;
      const items = (Array.isArray(response) ? response : (raw?.data ?? raw?.items ?? [])) as AuditLogEntry[];
      runInAction(() => {
        this.logs = items;
        this.logsOffset = items.length;
        this.logsHasMore = items.length >= this.logsLimit;
        this.isLoadingLogs = false;
      });
    } catch (error) {
      runInAction(() => { this.isLoadingLogs = false; });
      toastStore.showError('Не удалось загрузить лог действий');
      console.error('Failed to load audit logs:', error);
    }
  };

  loadMoreLogs = async (clientUuid: string) => {
    if (this.isLoadingLogs || !this.logsHasMore) return;
    this.isLoadingLogs = true;
    try {
      const response = await auditLogsList({ clientUuid, limit: this.logsLimit, offset: this.logsOffset });
      const raw = response as Record<string, unknown>;
      const items = (Array.isArray(response) ? response : (raw?.data ?? raw?.items ?? [])) as AuditLogEntry[];
      runInAction(() => {
        this.logs = [...this.logs, ...items];
        this.logsOffset += items.length;
        this.logsHasMore = items.length >= this.logsLimit;
        this.isLoadingLogs = false;
      });
    } catch (error) {
      runInAction(() => { this.isLoadingLogs = false; });
      console.error('Failed to load more audit logs:', error);
    }
  };
}

export const clientDetailStore = new ClientDetailStore();
